import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config';

const CURRENCY_STORAGE_KEY = 'naijanest_currency';

export const SUPPORTED_CURRENCIES = [
  { code: 'NGN', symbol: '₦', label: 'Nigerian Naira' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'CAD', symbol: 'CA$', label: 'Canadian Dollar' },
];

const CurrencyContext = createContext(null);

function getInitialCurrency() {
  try {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (SUPPORTED_CURRENCIES.some((c) => c.code === stored)) {
      return stored;
    }
  } catch {
    // Ignore storage failures and fall back to NGN.
  }
  return 'NGN';
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(getInitialCurrency);
  const [rates, setRates] = useState({ NGN: 1 });
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetch(`${API_BASE}/exchange-rates`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to load exchange rates');
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setRates(data.rates || { NGN: 1 });
          setStatus('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = useCallback((code) => {
    setCurrencyState(code);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, code);
    } catch {
      // Ignore storage failures - the selection still works for this session.
    }
  }, []);

  // Falls back to NGN whenever rates aren't ready/failed, even if the user
  // already picked another currency - a page should never show NaN/$0, it
  // should just stay in NGN until real rates are available.
  const convert = useCallback(
    (amountInNgn) => {
      const rate = rates[currency];
      if (currency === 'NGN' || !rate || status !== 'ready') {
        return { value: Number(amountInNgn) || 0, currency: 'NGN' };
      }
      return { value: (Number(amountInNgn) || 0) * rate, currency };
    },
    [currency, rates, status]
  );

  const value = useMemo(
    () => ({ currency, setCurrency, rates, status, convert }),
    [currency, setCurrency, rates, status, convert]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return ctx;
}
